import { EventMeta } from '../../../interfaces/meta.interface';

type PageViewedFields = {
    ObjectId: string;
};

const pageViewed: EventMeta<PageViewedFields> = {
    title: 'SharePoint Page Viewed',
    default: {
        description: 'SharePoint Page Viewed',
        longDescription: 'A page was viewed on a SharePoint site.',
        severity: 'info',
    },
    fieldsExtractor: (log: any) => {
        return {
            ObjectId: log.ObjectId,
        };
    },
    classifications: [
        // page viewed. fraudulent IP. not in allowed list
        {
            severity: 'danger',
            description: (fields) => `A suspicious IP viewed a SharePoint page`,
            longDescription: (fields) =>
                `A page on a SharePoint site was viewed from a suspicious IP address that is not in your allowed list. This activity may indicate an attempt to access site content without authorization.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // page viewed. user in org. fraudulent IP, allowed IP
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed IP list viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site. The action originated from a suspicious IP included in your allowed IP list. While the IP range itself is normally trusted, the individual IP has been flagged as suspicious, which makes the activity unusual. Review this page access to confirm whether it was expected or potentially unauthorized.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // page viewed. user not in org. fraudulent IP, allowed IP
        {
            severity: 'info',
            description: (fields) =>
                `A suspicious IP within your allowed IP list used a non-org account to view a page`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site with an account that does not belong to your organisation. The action originated from a suspicious IP included in your allowed IP list. While the IP range itself is normally trusted, the specific IP has been flagged as suspicious, and combined with the use of an external account, the activity is unusual. Review this page access to confirm whether it was expected or potentially unauthorized.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // page viewed. user in org. fraudulent IP, in allowed region.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed region viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site. The action originated from a suspicious IP located in your allowed region. Although the geographic location is normally trusted, the IP itself has been flagged as suspicious, which makes the activity unusual. Review this page view to confirm whether it was expected or potentially unauthorized.`,
            predicates: {
                isAllowedRegion: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // page viewed. user not in org. fraudulent IP, in allowed region.
        {
            severity: 'info',
            description: (fields) => `A suspicious IP within your allowed region used a non-org account to view a page`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site with an account that does not belong to your organisation. The action originated from a suspicious IP located in your allowed region. Although the geographic origin is normally considered trusted, the IP itself has been flagged as suspicious, and combined with the use of an external account, the activity is unusual. Review this page view to determine whether the access was legitimate or potentially unauthorized.`,
            predicates: {
                isAllowedRegion: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // page viewed. user in org. fraudulent IP, in allowed countries.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed countries viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site. The action originated from a suspicious IP located in one of your allowed countries. While the country itself is normally considered trusted, the individual IP address has been flagged as suspicious, making the activity unusual. Review this access to confirm whether it was legitimate or potentially unauthorized.`,
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // page viewed. user not in org. fraudulent IP, in allowed countries.
        {
            severity: 'info',
            description: (fields) =>
                `A suspicious IP within your allowed countries used a non-org account to view a page`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site with an account that does not belong to your organisation. The action originated from a suspicious IP located in one of your allowed countries. While the country itself aligns with your access policies, the IP has been flagged as suspicious. Combined with the use of an external account, this makes the event unusual. Review the access to confirm whether it was legitimate or potentially unauthorized.`,
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // page viewed. user in org. fraudulent IP, in allowed asn.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed ASN list viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site. The action originated from a suspicious IP included in your allowed ASN list. While the ASN itself is normally trusted, the specific IP has been flagged as suspicious, which makes the access unusual. Review this page view to determine whether it was expected or potentially unauthorized.`,
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // page viewed. user not in org. fraudulent IP, in allowed asn.
        {
            severity: 'info',
            description: (fields) =>
                `A suspicious IP within your allowed ASN list used a non-org account to view a page`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site with an account that does not belong to your organisation. The action originated from a suspicious IP that belongs to your allowed ASN list. While the ASN itself is trusted, the individual IP has been flagged as suspicious. Combined with the use of an external account, this makes the activity unusual. Review this page view to determine whether the access was expected or potentially unauthorized.`,
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // page viewed. user in org. not in allowed list
        {
            severity: 'warning',
            description: (fields) => `An unapproved IP viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site. The action originated from an unapproved IP outside your allowed list. While viewing pages on SharePoint is common, access from an unapproved IP makes the event unusual. Review the details to confirm whether this activity was expected or potentially unauthorized.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // page viewed. user not in org. not in allowed list
        {
            severity: 'info',
            description: (fields) => `An unapproved IP used a non-org account to view a page`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site with an account that does not belong to your organisation. The action originated from an unapproved IP outside your allowed list. While viewing SharePoint pages is routine, the combination of an external account and an unapproved IP makes the activity unusual. Review this access to confirm whether it was legitimate or potentially unauthorized.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // page viewed. microsoft activity
        {
            severity: 'info',
            description: (fields) => `A Microsoft system operation (synchronization server IP) viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site. This was logged as a Microsoft system operation event, with the IP address tied to the server handling synchronization. This entry reflects expected background activity and is informational in nature. No action is required unless such activity appears inconsistent with your configuration or usage expectations.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        // page viewed.
        {
            severity: 'info',
            description: (fields) => `A SharePoint page was viewed`,
            longDescription: (fields) =>
                `A page was viewed on a SharePoint site. This is an informational record of standard site activity. No unusual conditions were associated with this entry. No immediate action is required unless the page view was unexpected or coincides with other anomalies.`,
        },
    ],
};

export default pageViewed;
